/**
 * Search-and-pick another ticket — the shared search box behind both Merge
 * and Link (Step 21). Excludes the current ticket from results; the caller
 * owns what happens on pick.
 */
import { useEffect, useRef, useState } from 'react';
import { ticketsApi, type TicketRow } from './api';
import { Input } from '../../components/ui/input';
import { ticketRef } from '@itsm/shared';

export function TicketPicker({ excludeId, onPick }: { excludeId: string; onPick: (t: TicketRow) => void }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TicketRow[]>([]);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    debounce.current = setTimeout(async () => {
      try {
        const res = await ticketsApi.search(query.trim());
        setResults(res.rows.filter((r) => r.id !== excludeId));
      } catch {
        setResults([]);
      }
    }, 250);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [query, excludeId]);

  return (
    <div>
      <Input
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search by subject or #ref…"
      />
      {results.length > 0 && (
        <div className="mt-2 max-h-64 space-y-0.5 overflow-y-auto rounded-md border">
          {results.map((t) => (
            <button
              key={t.id}
              onClick={() => onPick(t)}
              className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm hover:bg-accent"
            >
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                {ticketRef(t.type, t.number)}
              </span>
              <span className="min-w-0 flex-1 truncate">{t.subject}</span>
            </button>
          ))}
        </div>
      )}
      {query.trim().length >= 2 && results.length === 0 && (
        <p className="mt-2 text-xs text-muted-foreground">No matching tickets.</p>
      )}
    </div>
  );
}
